import { useState } from "react";
import { Link } from "react-router-dom";
import { PublicValidatedUser } from "../../../../shared/types";
import { useLocalization } from "../../../hooks/use-localization/useLocalization";
import { useNetwork } from "../../../hooks/useNetwork/useNetwork";
import { getErrorText } from "../../../util/error/getErrorText";
import { useApp } from "../../../hooks/use-app/useApp";
import { PasswordInput } from "../../../components/input/passwordInput";
import { Providers } from "../../../components/providers/providers";
import { NetworkError } from "../../../util/error/networkError";
import { useQuery } from "../../../hooks/use-query/useQuery";
import { Form } from "../../../components/form/form";

export const LoginForm: React.FC<{
  user: PublicValidatedUser;
  onLogin: (redirectUrl: string) => any;
}> = ({ user, onLogin }) => {
  const query = useQuery();
  const app = useApp();
  const { localize } = useLocalization();
  const { loading, network } = useNetwork();
  const [error, setError] = useState<string>("");
  const [otpRequired, setOtpRequired] = useState<boolean>(
    query.otpRequired || false,
  );
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const validProviders = app.providers.filter((p) =>
    user.providers.includes(p.slug),
  );
  const alternativeProviders = validProviders.filter((p) => p.slug !== "email");
  const hasEmailProvider = validProviders.some((p) => p.slug === "email");

  return (
    <>
      {user.name && <h2 className="h3">{localize("hello", user.name)}</h2>}
      {(hasEmailProvider || otpRequired) && (
        <Form
          action="/login"
          method="POST"
          actionLabel={localize("login:action")}
          error={error}
          onSubmit={async (e) => {
            e.preventDefault();

            if (loading) {
              return;
            }

            const email = e.currentTarget.email.value;
            const password = e.currentTarget.password.value;
            const otp = e.currentTarget.otp?.value;

            try {
              const response = await network<{ redirect: string }>("/login", {
                method: "POST",
                json: { email, password, otp },
              });

              onLogin(response.redirect);
            } catch (error) {
              if (
                error instanceof NetworkError &&
                error.message === "otpRequired"
              ) {
                setOtpRequired(true);
                setEmail(email);
                setPassword(password);
              } else {
                setError(
                  getErrorText(error, {
                    "invalid code": localize("error:wrongOtp"),
                    wrongUsernameOrPassword: localize("error:badCredentials"),
                    429: localize("error:rateLimitExceeded"),
                  }),
                );
              }
            }
          }}
        >
          {otpRequired && email && password ? (
            <>
              <input type="hidden" name="email" defaultValue={email} />
              <input type="hidden" name="password" defaultValue={password} />
            </>
          ) : (
            <>
              <label>
                {localize("email")}
                <input
                  type="email"
                  name="email"
                  placeholder="min@e-post.no"
                  defaultValue={user.email}
                  required
                />
              </label>
              <PasswordInput
                label={
                  <>
                    {localize("password")}
                    <Link to={`/${app.name}/glemt-passord`}>
                      {localize("login:forgotPassword")}
                    </Link>
                  </>
                }
                name="password"
                required
              />
            </>
          )}
          {otpRequired && (
            <label>
              {localize("otp")}
              <input type="string" name="otp" autoComplete="off" required />
            </label>
          )}
        </Form>
      )}
      {!otpRequired && (
        <Providers
          hasEmailProvider={hasEmailProvider}
          providers={alternativeProviders}
        />
      )}
    </>
  );
};
